import React, { ComponentProps, FocusEvent, MouseEventHandler, ReactNode } from 'react';
import CellAction from './CellAction';
import CellExpand from './CellExpand';
import ChildRowDeleteButton from './ChildRowDeleteButton';
// @ts-ignore
import onClickOutside from 'react-onclickoutside';
import deepEqual from '../connectedForm/utils/deepEqual';
import isFunction, { isReactMemoOrLazy } from './utils/isFunction';
import ContextOptionsFormatter from '../grid-addons/formatters/ContextOptionsFormatter';

import joinClasses from 'classnames';
import EditorContainer from './editors/EditorContainer';
import { ICellMetaDataShape } from './PropTypeShapes/CellMetaDataShape';
import SimpleCellFormatter from '../grid-addons/formatters/SimpleCellFormatter';
import ColumnUtils from './ColumnUtils';
import { IDependentValues } from '../../@types/workspaceTypes';
import { Tooltip } from '@fluentui/react-components';

require('styles/react-data-grid/react-data-grid-cell.css');

const EditorContainerClickOutside = onClickOutside(EditorContainer);

type ICellProps = {
  rowIdx: number;
  idx: number;
  selected: { idx: number };
  selectedColumn: any;
  height: number;
  tabIndex: number;
  column: any;
  value: any;
  isExpanded: boolean;
  isRowSelected: boolean;
  cellMetaData: ICellMetaDataShape;
  handleDragStart: Function;
  className: string;
  cellControls: any;
  rowData: any;
  forceUpdate: boolean;
  expandableOptions: any;
  isScrolling: boolean;
  tooltip: string;
  isCellValueChanging: Function;
  children: ReactNode | Array<ReactNode>;
  testId?: string; //Unusual, expecting data-testid, safest not to change for the moment.
};

type ICellState = {
  isCellValueChanging: boolean;
  isLockChanging: boolean;
  modalOpen: boolean;
};

class Cell extends React.Component<ICellProps, ICellState> {
  static defaultProps = {
    isExpanded: false,
    value: '',
    isCellValueChanging: (value: any, nextValue: any) => value !== nextValue,
  };
  state = {
    isCellValueChanging: false,
    isLockChanging: false,
    modalOpen: false,
  };
  node: HTMLDivElement | null = null;

  constructor(props: ICellProps) {
    super(props);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  componentDidMount() {
    this.checkFocus();
  }

  componentDidUpdate(prevProps: ICellProps) {
    const isCellValueChanging = this.props.isCellValueChanging(prevProps.value, this.props.value);
    const isLockChanging = prevProps.column.locked !== this.props.column.locked;

    if (isCellValueChanging !== this.state.isCellValueChanging || isLockChanging !== this.state.isLockChanging) {
      this.setState({
        isCellValueChanging: isCellValueChanging,
        isLockChanging: isLockChanging,
      });
    }

    this.checkFocus();
    const dragged = this.props.cellMetaData.dragged;

    if (dragged && dragged.complete === true) {
      this.props.cellMetaData.handleTerminateDrag();
    }

    if (
      this.state.isCellValueChanging &&
      this.props.selectedColumn !== null &&
      this.props.selectedColumn !== undefined
    ) {
      this.applyUpdateClass();
    }

    if (this.state.isLockChanging && !this.props.column.locked) {
      this.removeScroll();
    }
  }

  shouldComponentUpdate(nextProps: Readonly<ICellProps>, nextState: Readonly<ICellState>, nextContext: any): boolean {
    return (
      this.props.column.width !== nextProps.column.width ||
      this.props.column.left !== nextProps.column.left ||
      this.props.column.cellClass !== nextProps.column.cellClass ||
      this.props.height !== nextProps.height ||
      this.props.rowIdx !== nextProps.rowIdx ||
      this.isCellSelectionChanging(nextProps) ||
      this.isDraggedCellChanging(nextProps) ||
      this.isCopyCellChanging(nextProps) ||
      this.props.isRowSelected !== nextProps.isRowSelected ||
      this.isSelected() ||
      this.props.isCellValueChanging(this.props.value, nextProps.value) ||
      this.props.forceUpdate ||
      this.props.className !== nextProps.className ||
      this.props.expandableOptions?.expanded !== nextProps.expandableOptions?.expanded ||
      this.hasChangedDependentValues(nextProps) ||
      this.props.column.locked !== nextProps.column.locked
    );
  }

  onCellClick = (e: MouseEvent) => {
    const meta = this.props.cellMetaData;

    if (meta?.onCellClick && typeof meta.onCellClick === 'function') {
      meta.onCellClick(
        {
          rowIdx: this.props.rowIdx,
          idx: this.props.idx,
        },
        e
      );
    }
  };
  onCellFocus = (e: FocusEvent<any>) => {
    const meta = this.props.cellMetaData;

    if (meta?.onCellFocus && typeof meta.onCellFocus === 'function') {
      meta.onCellFocus(
        {
          rowIdx: this.props.rowIdx,
          idx: this.props.idx,
        },
        e
      );
    }
  };

  onCellContextMenu = (e: Event) => {
    const meta = this.props.cellMetaData;

    if (meta && meta.onCellContextMenu && typeof meta.onCellContextMenu === 'function') {
      meta.onCellContextMenu({
        rowIdx: this.props.rowIdx,
        idx: this.props.idx,
        event: e,
      });
    }
  };
  onCellDoubleClick = (e: Event) => {
    const meta = this.props.cellMetaData;

    if (meta?.onCellDoubleClick && typeof meta.onCellDoubleClick === 'function') {
      meta.onCellDoubleClick(
        {
          rowIdx: this.props.rowIdx,
          idx: this.props.idx,
        },
        e
      );
    }
  };
  onCellExpand = (e: Event) => {
    e.stopPropagation();
    const meta = this.props.cellMetaData;
    meta?.onCellExpand?.({
      rowIdx: this.props.rowIdx,
      idx: this.props.idx,
      rowData: this.props.rowData,
      expandArgs: this.props.expandableOptions,
    });
  };
  onCellKeyDown = (e: KeyboardEvent) => {
    if (this.canExpand() && e.key === 'Enter') {
      this.onCellExpand(e);
    }
  };
  onDeleteSubRow = () => {
    const meta = this.props.cellMetaData;

    meta?.onDeleteSubRow?.({
      rowIdx: this.props.rowIdx,
      idx: this.props.idx,
      rowData: this.props.rowData,
      expandArgs: this.props.expandableOptions,
    });
  };
  onDragHandleDoubleClick: MouseEventHandler<any> = (e) => {
    e.stopPropagation();
    const meta = this.props.cellMetaData;

    if (meta?.onDragHandleDoubleClick && typeof meta.onDragHandleDoubleClick === 'function') {
      meta.onDragHandleDoubleClick({
        rowIdx: this.props.rowIdx,
        idx: this.props.idx,
        rowData: this.getRowData(),
        e,
      });
    }
  };
  onDragOver = (e: Event) => {
    e.preventDefault();
  };
  getStyle = (dependentValues: IDependentValues = {} as IDependentValues, warning?: boolean, info?: boolean) => {
    const { cellStyle = {} } = dependentValues;

    const style = {
      position: 'absolute',
      width: this.props.column.width,
      height: this.props.height,
      left: this.props.column.left,
      contain: 'layout',
      ...cellStyle,
    };

    if (dependentValues.message) {
      style.backgroundColor = '#ffa000';
      style.color = 'white';
      style.fontWeight = 'bold';
    }

    if (warning) {
      style.border = `1px solid #ff6f47`;
    } else if (info) {
      style.border = `1px solid #47b5ff`;
    }

    return style;
  };
  updateCellState = (state: ICellState) => {
    this.setState(state);
  };

  handleClickOutside(ev: Event) {
    if (this.isActive()) {
      const column = this.props?.column;

      if (
        this.props?.column?.formatterType === 'date' ||
        column?.key?.toLowerCase()?.includes('date') ||
        column?.name?.toLowerCase()?.includes('date')
      ) {
        return;
      }

      this.props.cellMetaData?.onCommitCancel();
    }

    //const creatable = getIn(this.props, 'column.options.dropdown.creatable');
    //if (creatable) {
    //Preserve usage per AKD advice (Bug fix for adding of new items, such as prices).
    //Fix for selection modals (I.e. products) - Don't want to prevent default or we can't interact with the Modal.
    //  ev.preventDefault();
    //}
    ev.preventDefault();
  }

  getFormatter = () => {
    const col = this.props.column;
    const testid = `${this.props.testId}-cell-${this.props.rowIdx}-${col.key}-editor`;

    if (this.isActive()) {
      const editorType = col.editor && col.editor.type ? col.editor.type.name : null;
      const EditorComponent =
        editorType && editorType !== 'DateInputEditor' && editorType !== 'DropdownEditor'
          ? EditorContainer
          : EditorContainerClickOutside;
      return (
        <EditorComponent
          key={`${this.props.rowIdx}-${col.key}-editor`}
          testId={testid}
          rowData={this.getRowData()}
          rowIdx={this.props.rowIdx}
          value={this.props.value}
          idx={this.props.idx}
          cellMetaData={this.props.cellMetaData}
          column={col}
          height={this.props.height}
          updateCellState={this.updateCellState}
          handleClickOutside={this.handleClickOutside}
          modalOpen={this.state.modalOpen}
        />
      );
    }

    return this.props.column.formatter;
  };
  getRowData = (props = this.props) => {
    return props.rowData.toJSON ? props.rowData.toJSON() : props.rowData;
  };
  getFormatterDependencies = () => {
    // convention based method to get corresponding Id or Name of any Name or Id property
    if (typeof this.props.column.getRowMetaData === 'function') {
      return this.props.column.getRowMetaData(this.getRowData(), this.props.column);
    }
  };
  getCellClass = () => {
    const className = joinClasses(
      this.props.column.cellClass,
      'react-grid-Cell',
      this.props.className,
      this.props.column.locked ? 'react-grid-Cell--locked' : null
    );
    const extraClasses = joinClasses({
      'row-selected': this.props.isRowSelected,
      'row-soft-selected': this.props.isRowSelected && this.props.cellMetaData?.enableCellSelect,
      editing: this.isActive(),
      copied: this.isCopied() || this.wasDraggedOver() || this.isDraggedOverUpwards() || this.isDraggedOverDownwards(),
      'is-dragged-over-up': this.isDraggedOverUpwards(),
      'is-dragged-over-down': this.isDraggedOverDownwards(),
      'was-dragged-over': this.wasDraggedOver(),
      'cell-tooltip': !!this.props.tooltip,
      'rdg-child-cell':
        this.props.expandableOptions &&
        this.props.expandableOptions.subRowDetails &&
        this.props.expandableOptions.treeDepth > 0,
      'last-column': this.props.column.isLastColumn,
    });
    return joinClasses(className, extraClasses);
  };
  getUpdateCellClass = () => {
    return this.props.column.getUpdateCellClass
      ? this.props.column.getUpdateCellClass(
          this.props.selectedColumn,
          this.props.column,
          this.state.isCellValueChanging
        )
      : '';
  };
  isColumnSelected = () => {
    const meta = this.props.cellMetaData;

    if (!meta) {
      return false;
    }

    return meta.selected && meta.selected.idx === this.props.idx;
  };
  isSelected = () => {
    const meta = this.props.cellMetaData;

    if (!meta) {
      return false;
    }

    return meta.selected && meta.selected.rowIdx === this.props.rowIdx && meta.selected.idx === this.props.idx;
  };
  isActive = () => {
    if (this.state.modalOpen) return true;
    const meta = this.props.cellMetaData;

    if (!meta || this.props.column.formatter === ContextOptionsFormatter) {
      return false;
    }

    return this.isSelected() && meta.selected.active === true;
  };
  isCellSelectionChanging = (nextProps: {
    idx: number;
    cellMetaData: {
      selected: {
        idx: number;
      };
    };
  }): boolean => {
    const meta = this.props.cellMetaData;

    if (!meta) {
      return false;
    }

    const nextSelected = nextProps.cellMetaData.selected;

    if (meta.selected && nextSelected) {
      return this.props.idx === nextSelected.idx || this.props.idx === meta.selected.idx;
    }

    return true;
  };
  isCellSelectEnabled = () => {
    const meta = this.props.cellMetaData;

    if (!meta) {
      return false;
    }

    return meta.enableCellSelect;
  };
  hasChangedDependentValues = (nextProps: ICellProps) => {
    const currentColumn = this.props.column;
    let hasChangedDependentValues = false;
    const nextColumn = nextProps.column;

    if (currentColumn.getRowMetaData && nextColumn.getRowMetaData) {
      const currentRowMetaData = currentColumn.getRowMetaData(this.getRowData(), currentColumn);
      const nextRowMetaData = nextColumn.getRowMetaData(this.getRowData(nextProps), nextColumn);
      hasChangedDependentValues = !deepEqual(currentRowMetaData, nextRowMetaData);
    }

    return hasChangedDependentValues;
  };
  applyUpdateClass = () => {
    const updateCellClass = this.getUpdateCellClass();

    // -> removing the class
    if (updateCellClass !== null && updateCellClass !== undefined && updateCellClass !== '' && this.node) {
      const cellDOMNode = this.node;

      if (cellDOMNode.classList) {
        cellDOMNode.classList.remove(updateCellClass);
        // -> and re-adding the class
        cellDOMNode.classList.add(updateCellClass);
      } else if (cellDOMNode.className.indexOf(updateCellClass) === -1) {
        // IE9 doesn't support classList, nor (I think) altering element.className
        // without replacing it wholesale.
        cellDOMNode.className = `${cellDOMNode.className} ${updateCellClass}`;
      }
    }
  };
  setScrollLeft = (scrollLeft: number) => {
    const node = this.node;

    if (node) {
      const transform = `translate3d(${scrollLeft}px, 0px, 0px)`;
      node.style.webkitTransform = transform;
      node.style.transform = transform;
    }
  };
  removeScroll = () => {
    const node = this.node;

    if (node) {
      node.style.webkitTransform = '';
      node.style.transform = '';
    }
  };
  isCopied = (): boolean => {
    const copied = this.props.cellMetaData.copied;
    return copied && copied.rowIdx === this.props.rowIdx && copied.idx === this.props.idx;
  };
  isDraggedOver = (): boolean => {
    const dragged = this.props.cellMetaData.dragged;
    return dragged && dragged.overRowIdx === this.props.rowIdx && dragged.idx === this.props.idx;
  };
  wasDraggedOver = (): boolean => {
    const dragged = this.props.cellMetaData.dragged;
    return (
      dragged &&
      ((dragged.overRowIdx < this.props.rowIdx && this.props.rowIdx < dragged.rowIdx) ||
        (dragged.overRowIdx > this.props.rowIdx && this.props.rowIdx > dragged.rowIdx)) &&
      dragged.idx === this.props.idx
    );
  };
  isDraggedCellChanging = (nextProps: any): boolean => {
    let isChanging;
    const dragged = this.props.cellMetaData.dragged;
    const nextDragged = nextProps.cellMetaData.dragged;

    if (dragged) {
      isChanging = (nextDragged && this.props.idx === nextDragged.idx) || (dragged && this.props.idx === dragged.idx);
      return isChanging;
    }

    return false;
  };
  isCopyCellChanging = (nextProps: any): boolean => {
    let isChanging;
    const copied = this.props.cellMetaData.copied;
    const nextCopied = nextProps.cellMetaData.copied;

    if (copied) {
      isChanging = (nextCopied && this.props.idx === nextCopied.idx) || (copied && this.props.idx === copied.idx);
      return isChanging;
    }

    return false;
  };
  isDraggedOverUpwards = (): boolean => {
    const dragged = this.props.cellMetaData.dragged;
    return !this.isSelected() && this.isDraggedOver() && this.props.rowIdx < dragged.rowIdx;
  };
  isDraggedOverDownwards = (): boolean => {
    const dragged = this.props.cellMetaData.dragged;
    return !this.isSelected() && this.isDraggedOver() && this.props.rowIdx > dragged.rowIdx;
  };
  isFocusedOnBody = () => {
    const { nodeName } = document.activeElement as any;
    return (
      document.activeElement === null ||
      document.activeElement === undefined ||
      (nodeName &&
        typeof (nodeName as any) === 'string' &&
        (nodeName.toLowerCase() === 'body' || nodeName.toLowerCase() === 'reactmodal__body--open'))
    );
  };
  // New function to check if parent element of this grid is the one that has focus
  // Issue with multiple grids in one form grabbing focus
  getFocusedParent = (elem: any) => {
    const thisGrid = this.props.cellMetaData.getDataGridDOMNode?.();

    for (let thisElem = elem; thisElem && thisElem !== document; thisElem = thisElem.parentNode) {
      if (thisElem.className.indexOf('react-grid-Container') !== -1 && thisElem === thisGrid) return true;
    }

    return false;
  };
  isFocusedOnCell = () => {
    const activeEl = document.activeElement;
    const focusedOnCell = activeEl && activeEl.className.indexOf('react-grid-Cell');
    if (focusedOnCell === -1) return false;
    return this.getFocusedParent(activeEl);
  };
  checkFocus = () => {
    if (this.isSelected() && !this.isActive()) {
      if (
        this.props.isScrolling &&
        !this.props.cellMetaData.isScrollingVerticallyWithKeyboard &&
        !this.props.cellMetaData.isScrollingHorizontallyWithKeyboard
      ) {
        return;
      }

      // If the enableCellAutoFocus is set in the ReactDataGrid props, it will allow the cell to take focus when the browser is focused on the body.
      // Otherwise, only focus to the current cell if the currently active node in the document is within the data grid.
      // Meaning focus should not be stolen from elements that the grid doesn't control.
      const cellAutoFocusEnabled = this.props.cellMetaData && this.props.cellMetaData.enableCellAutoFocus;
      const dataGridDOMNode =
        this.props.cellMetaData && this.props.cellMetaData.getDataGridDOMNode
          ? this.props.cellMetaData.getDataGridDOMNode()
          : null;

      if (
        this.isFocusedOnCell() ||
        (cellAutoFocusEnabled && this.isFocusedOnBody()) ||
        (dataGridDOMNode && dataGridDOMNode.contains(document.activeElement))
      ) {
        const cellDOMNode = this.node;

        if (cellDOMNode) {
          cellDOMNode.focus();
        }
      }
    }
  };
  canEdit = () => {
    return this.props.column.editor || this.props.column.editable;
  };
  canExpand = () => {
    return this.props.expandableOptions && this.props.expandableOptions.canExpand;
  };
  createColumnEventCallBack = (onColumnEvent: any, info: unknown) => {
    return (e: any) => {
      onColumnEvent(e, info);
    };
  };
  createCellEventCallBack = (gridEvent: any, columnEvent: any) => {
    return (e: any) => {
      gridEvent(e);
      columnEvent(e);
    };
  };
  createEventDTO = (gridEvents: any, columnEvents: any, onColumnEvent: any) => {
    const allEvents = Object.assign({}, gridEvents);

    for (const eventKey in columnEvents) {
      // eslint-disable-next-line no-prototype-builtins
      if (columnEvents.hasOwnProperty(eventKey)) {
        const eventInfo = {
          idx: this.props.idx,
          rowIdx: this.props.rowIdx,
          rowId: this.props.rowData[this.props.cellMetaData.rowKey!],
          name: eventKey,
        };
        const eventCallback = this.createColumnEventCallBack(onColumnEvent, eventInfo);

        // eslint-disable-next-line no-prototype-builtins
        if (allEvents.hasOwnProperty(eventKey)) {
          const currentEvent = allEvents[eventKey];
          allEvents[eventKey] = this.createCellEventCallBack(currentEvent, eventCallback);
        } else {
          allEvents[eventKey] = eventCallback;
        }
      }
    }

    return allEvents;
  };
  getEvents = () => {
    const columnEvents = this.props.column ? Object.assign({}, this.props.column.events) : undefined;
    const onColumnEvent = this.props.cellMetaData ? this.props.cellMetaData.onColumnEvent : undefined;
    const gridEvents = {
      onClick: (e: MouseEvent) => {
        e.stopPropagation();
        this.onCellClick(e);
      },
      onFocus: this.onCellFocus,
      onDoubleClick: this.onCellDoubleClick,
      onContextMenu: this.onCellContextMenu,
      onDragOver: this.onDragOver,
    };

    if (!columnEvents || !onColumnEvent) {
      return gridEvents;
    }

    return this.createEventDTO(gridEvents, columnEvents, onColumnEvent);
  };

  getCellActions() {
    const { cellMetaData, column, rowData } = this.props;

    if (cellMetaData && cellMetaData.getCellActions) {
      const cellActions = cellMetaData.getCellActions(column, rowData);

      if (cellActions && cellActions.length) {
        return cellActions.map((action: ComponentProps<typeof CellAction>['action'], index: number) => {
          // eslint-disable-next-line react/no-array-index-key
          return <CellAction key={index} action={action} isFirst={index === 0} />;
        });
      }

      return null;
    }

    return null;
  }

  renderCellContent = (
    props: { value: any; column: any; rowIdx: number; idx: number; isExpanded: boolean },
    dependentValues: IDependentValues
  ) => {
    let CellContent;
    const Formatter = this.getFormatter();

    if (React.isValidElement(Formatter)) {
      const clonedProps = { ...props, dependentValues, cellMetaData: this.props.cellMetaData };
      CellContent = React.cloneElement(Formatter, clonedProps);
    } else if (isFunction(Formatter) || isReactMemoOrLazy(Formatter)) {
      const dependentValuesProp = Object.assign({}, dependentValues, {
        rowIdx: this.props.rowIdx,
        cellMetaData: this.props.cellMetaData,
      });
      CellContent = <Formatter dependentValues={dependentValuesProp} {...props} />;
    } else {
      CellContent = <SimpleCellFormatter value={this.props.value} />;
    }

    const isExpandCell = this.props.expandableOptions
      ? this.props.expandableOptions.field === this.props.column.key
      : false;
    const treeDepth = this.props.expandableOptions ? this.props.expandableOptions.treeDepth : 0;
    const marginLeft = this.props.expandableOptions && isExpandCell ? this.props.expandableOptions.treeDepth * 30 : 0;
    let cellExpander;
    let cellDeleter;

    if (this.canExpand()) {
      cellExpander = <CellExpand expandableOptions={this.props.expandableOptions} onCellExpand={this.onCellExpand} />;
    }

    const isDeleteSubRowEnabled = !!this.props.cellMetaData.onDeleteSubRow;

    if (treeDepth > 0 && isExpandCell) {
      cellDeleter = (
        <ChildRowDeleteButton
          treeDepth={treeDepth}
          cellHeight={this.props.height}
          siblingIndex={this.props.expandableOptions.subRowDetails.siblingIndex}
          numberSiblings={this.props.expandableOptions.subRowDetails.numberSiblings}
          onDeleteSubRow={this.onDeleteSubRow}
          isDeleteSubRowEnabled={isDeleteSubRowEnabled}
        />
      );
    }

    return (
      <div className="react-grid-Cell__value">
        {cellDeleter}
        <div
          style={{
            marginLeft: marginLeft,
          }}
        >
          <span>{CellContent}</span> {this.props.cellControls} {cellExpander}
        </div>
      </div>
    );
  };

  /**
   * Override display of the *whole* cell (Required if we want to override padding/etc that is otherwise imposed on cell formatters).
   */
  renderCellFormatter() {
    const dependentValues = this.getFormatterDependencies();

    const CellFormatter = this.props.column.cellFormatter;
    return (
      <CellFormatter
        value={this.props.value}
        column={this.props.column}
        rowIdx={this.props.rowIdx}
        isExpanded={this.props.isExpanded}
        dependentValues={{ ...dependentValues, rowIdx: this.props.rowIdx, cellMetaData: this.props.cellMetaData }}
        idx={this.props.idx}
      />
    );
  }

  render() {
    if (this.props.column.hidden) {
      return null;
    } else if (this.props.column.cellFormatter) {
      return this.renderCellFormatter();
    }

    const dependentValues = this.getFormatterDependencies();

    let info = false;
    let warning = false;

    if (dependentValues?.rowData) {
      if (dependentValues?.options?.warning && isFunction(dependentValues.options.warning)) {
        warning = dependentValues.options.warning(dependentValues.rowData);
      }

      if (!warning && dependentValues?.options?.info && isFunction(dependentValues.options.info)) {
        info = dependentValues.options.info(dependentValues.rowData);
      }
    }

    const style = this.getStyle(dependentValues, warning, info);
    const className = this.getCellClass();
    const cellActions = this.getCellActions();
    const cellContent =
      this.props.children ||
      this.renderCellContent(
        {
          value: this.props.value,
          column: this.props.column,
          rowIdx: this.props.rowIdx,
          idx: this.props.idx,
          isExpanded: this.props.isExpanded,
        },
        dependentValues
      );
    const dragHandle =
      !this.isActive() &&
      ColumnUtils.canEdit(this.props.column, this.props.rowData, this.props.cellMetaData.enableCellSelect) ? (
        <div className="drag-handle" draggable="true" onDoubleClick={this.onDragHandleDoubleClick}>
          <span
            style={{
              display: 'none',
            }}
          />
        </div>
      ) : null;
    const events = this.getEvents();

    const { value: dataValue, tabIndex } = this.props;

    const cell = (
      <div
        data-testid={`${this.props.testId}-cell-${this.props.rowIdx}-${this.props.column.key}`}
        data-value={dataValue}
        tabIndex={tabIndex}
        className={className}
        style={style}
        {...events}
        ref={(node) => {
          this.node = node;
        }}
      >
        {cellActions}
        {cellContent}
        {dragHandle}
      </div>
    );

    const cellTooltipContent =
      this.props.column?.cellTooltip?.(this.props.rowData) ??
      this.props.tooltip ??
      (dependentValues && dependentValues.message) ??
      warning ??
      info;
    if (cellTooltipContent) {
      return (
        <Tooltip content={cellTooltipContent} relationship={'label'} showDelay={0}>
          {cell}
        </Tooltip>
      );
    }

    return cell;
  }
}

export default Cell;
